import { renderMajorScoreSvg, renderMajorRankSvg } from "../lib/chart-renderer"
import { extractSourceDomains } from "../lib/seo"

<const/data=input.data>
<const/institution=data.institution>
<const/summary=data.summary>
<const/featuredRecords=data.featured_records>
<const/provinceSummaries=data.province_summaries.slice(0, 12)>
<const/yearSummaries=data.year_summaries>
<const/majorSummaries=data.major_summaries.slice(0, 12).reverse()>
<const/majorTrends=(data.major_trends || [])>
<const/latestYearSummary=(yearSummaries.length > 0 ? yearSummaries[yearSummaries.length - 1] : null)>
<const/scoreTrend=(latestYearSummary && latestYearSummary.score_direction ? latestYearSummary.score_direction : "stable")>
<const/scoreTrendLabel=(scoreTrend === "rising" ? "上升" : scoreTrend === "falling" ? "下降" : "平稳")>
<const/yoyLabel=(latestYearSummary && latestYearSummary.yoy_delta != null ? (latestYearSummary.yoy_delta >= 0 ? `+${latestYearSummary.yoy_delta}` : `${latestYearSummary.yoy_delta}`) : "—")>
<const/batchNames=(latestYearSummary && latestYearSummary.batch_names ? latestYearSummary.batch_names : [])>
<const/allBatchNames=(yearSummaries.reduce((acc, y) => { (y.batch_names || []).forEach(b => { if (!acc.includes(b)) acc.push(b) }); return acc }, []))>
<const/basePath=input.basePath>
<const/majorsPath=basePath + "/majors">
<const/provincesPath=basePath + "/provinces">
<const/yearsPath=basePath + "/years">
<const/relatedInstitutions=(input.relatedInstitutions || [])>
<const/hasDescription=(institution.description && institution.description.trim().length > 0)>
<const/descriptionFirstSection=(hasDescription ? institution.description.split("$$")[0].trim() : "")>
<const/descriptionLead=(descriptionFirstSection.length > 200 ? (function() { const raw = descriptionFirstSection.slice(0, 200); const cnPunc = "，。、；：！？）》」』"; let end = raw.length; while (end > 0 && cnPunc.includes(raw[end - 1])) end--; return raw.slice(0, end) + "…"; })() : descriptionFirstSection)>
<const/designationNames=(institution.designations || []).map(designation => designation.name)>
<const/descriptionPath=basePath + "/intro">
<const/shortlistEntry=({
  key: basePath,
  path: basePath,
  name: institution.name,
  institution_province: institution.province,
  city: institution.city,
  category: institution.category,
  designation_summary: designationNames.join(" · "),
  record_count: summary.record_count,
  province_count: summary.province_count,
  major_count: summary.major_count,
  year_count: summary.year_count,
  latest_year: summary.latest_year,
  source_label: "院校总览",
})>
<const/summaryLine=(`${institution.name}位于${institution.province}${institution.city ? ` · ${institution.city}` : ""}${designationNames.length ? `，属于${designationNames.join("、")}` : ""}${institution.category ? `，院校类型为${institution.category}` : ""}。当前页面覆盖${summary.province_count}个省份、${summary.year_count}个年份和${summary.record_count}条录取记录，可作为查询${institution.name}录取分数线的重要汇总页。`)>
<const/faqStructuredData=JSON.stringify({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: `${institution.name}目前覆盖多少省份和年份的录取数据？`,
      acceptedAnswer: {
        "@type": "Answer",
        text: `${institution.name}当前覆盖${summary.province_count}个省份、${summary.year_count}个年份，累计收录${summary.record_count}条录取记录。`,
      },
    },
    {
      "@type": "Question",
      name: `${institution.name}最新年份的录取数据是哪一年？`,
      acceptedAnswer: {
        "@type": "Answer",
        text: `当前页面最新年份为${summary.latest_year}年，可进入年份页面核对历年记录规模和覆盖情况。`,
      },
    },
    {
      "@type": "Question",
      name: `${institution.name}总览页主要承担什么作用？`,
      acceptedAnswer: {
        "@type": "Answer",
        text: `总览页用于集中展示${institution.name}的历年录取数据、重点专业、省份覆盖和年度分布，并为后续院校筛选提供基础判断依据。`,
      },
    },
  ],
})>

<institution-breadcrumb institutionName=institution.name basePath=basePath pageKind="overview" pageLabel="总览" />

<div class="grid-container">
  <nav class="local-nav">
    <ul class="menu">
      <li><a href=(`${basePath}`) class="active">总览</a></li>
      <if=hasDescription>
        <li><a href=(descriptionPath)>简介</a></li>
      </if>
      <li><a href=(`${majorsPath}`)>专业</a></li>
      <li><a href=(`${provincesPath}`)>省份</a></li>
      <li><a href=(`${yearsPath}`)>年份</a></li>
    </ul>
  </nav>
</div>

<section class="hero">
  <div class="grid-container">
    <div class="hero-panel">
      <h2 class="hero-title">${institution.name}</h2>
      <if=designationNames.length || institution.category>
        <div class="designation-strip">
          <for|name| of=designationNames>
            <span class="designation-badge">${name}</span>
          </for>
          <if=institution.category>
            <span class="designation-badge category">${institution.category}</span>
          </if>
        </div>
      </if>
      <if=batchNames.length>
        <div class="batch-strip">招生批次：${batchNames.join(" / ")}</div>
      </if>
      <if=hasDescription>
        <p class="page-lead institution-summary-lead">${descriptionLead} <a href=(descriptionPath)>查看更多</a></p>
      </if>
      <else>
        <p class="page-lead institution-summary-lead">${summaryLine}</p>
      </else>
      <div class="hero-actions">
        <shortlist-button entry=shortlistEntry buttonTone="secondary" />
      </div>
      <div class="grid-x grid-margin-x stats-strip">
        <div class="cell medium-3 small-6">
          <div class="stat-card">
            <div class="stat-value">${summary.min_score_floor ?? "—"} ~ ${summary.min_score_ceiling ?? "—"}</div>
            <div class="stat-label">分数区间</div>
          </div>
        </div>
        <div class="cell medium-3 small-6">
          <div class="stat-card">
            <div class="stat-value">${summary.latest_year}</div>
            <div class="stat-label">最新年份</div>
          </div>
        </div>
        <div class="cell medium-3 small-6">
          <div class="stat-card">
            <div class="stat-value">${yoyLabel}</div>
            <div class="stat-label">${scoreTrendLabel}</div>
          </div>
        </div>
        <div class="cell medium-3 small-6">
          <div class="stat-card">
            <div class="stat-value">${summary.province_count}</div>
            <div class="stat-label">覆盖省份</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</section>

<section class="section-block">
  <div class="grid-container">
    <div class="section-header">
      <div>
        <h2 class="section-title">历年分数线</h2>
        <p class="section-note">展示该校历年最低分、平均分、最高分的变化趋势，帮助判断录取难度走向。</p>
      </div>
    </div>
    <div class="data-panel table-wrap">
      <table class="stack">
        <thead>
          <tr>
            <th>年份</th>
            <th>最低分</th>
            <th>平均分</th>
            <th>最高分</th>
            <th>最低位次</th>
            <th>同比变化</th>
            <th>趋势</th>
          </tr>
        </thead>
        <tbody>
          <for|year| of=yearSummaries>
            <tr>
              <td><a href=(yearsPath)>${year.year}</a></td>
              <td>${year.min_score_floor ?? "—"}</td>
              <td>${year.avg_min_score ?? "—"}</td>
              <td>${year.min_score_ceiling ?? "—"}</td>
              <td>${year.min_rank ?? "—"}</td>
              <td>${year.yoy_delta != null ? (year.yoy_delta >= 0 ? `+${year.yoy_delta}` : `${year.yoy_delta}`) : "—"}</td>
              <td>
                <if=year.score_direction === "rising">
                  <span class="trend-badge trend-up">↑ 上升</span>
                </if>
                <else if=year.score_direction === "falling">
                  <span class="trend-badge trend-down">↓ 下降</span>
                </else>
                <else>
                  <span class="trend-badge trend-stable">→ 平稳</span>
                </else>
              </td>
            </tr>
          </for>
        </tbody>
      </table>
    </div>
  </div>
</section>

<if=majorTrends.length>
  <let/selectedIdx=0>
  <section class="section-block">
    <div class="grid-container">
      <div class="section-header">
        <div>
          <h2 class="section-title">专业历年录取走势</h2>
          <p class="section-note">选择专业查看历年分数及位次变化趋势。</p>
        </div>
      </div>
      <div class="major-trend-selector">
        <select class="major-trend-dropdown" value=selectedIdx onChange() { selectedIdx = parseInt(this.value) }>
          <for|trend, i| of=majorTrends>
            <option value=(i)>${trend.major_name}</option>
          </for>
        </select>
      </div>
      <for|trend, i| of=majorTrends>
        <const/scoreSvg=renderMajorScoreSvg(trend)/>
        <const/hasRank=(trend.min_ranks && trend.min_ranks.some(r => r != null))>
        <const/rankSvg=(hasRank ? renderMajorRankSvg(trend) : "")/>
        <div hidden=(i !== selectedIdx)>
          <div class="data-panel table-wrap">
            <table class="stack">
              <thead>
                <tr>
                  <th>年份</th>
                  <th>最低分</th>
                  <th>平均分</th>
                  <th>最高分</th>
                  <if=hasRank>
                    <th>最低位次</th>
                  </if>
                </tr>
              </thead>
              <tbody>
                <for|yearIdx| from=0 to=(trend.years.length - 1)>
                  <tr>
                    <td>${trend.years[yearIdx]}</td>
                    <td>${trend.min_scores[yearIdx] ?? "—"}</td>
                    <td>${trend.avg_scores[yearIdx] ?? "—"}</td>
                    <td>${trend.max_scores[yearIdx] ?? "—"}</td>
                    <if=hasRank>
                      <td>${trend.min_ranks[yearIdx] ?? "—"}</td>
                    </if>
                  </tr>
                </for>
              </tbody>
            </table>
          </div>
          <div class="content-panel chart-panel">
            $!{scoreSvg}
          </div>
          <if=hasRank>
            <div class="content-panel chart-panel">
              $!{rankSvg}
            </div>
          </if>
          <if=trend.province_records && trend.province_records.length>
            <h3 class="chart-heading">各省份录取分数（${trend.province_records[0].year} 年）</h3>
            <div class="data-panel table-wrap">
              <table class="stack">
                <thead>
                  <tr>
                    <th>省份</th>
                    <th>科类</th>
                    <th>批次</th>
                    <th>最低分</th>
                    <th>平均分</th>
                    <th>最高分</th>
                    <th>最低位次</th>
                  </tr>
                </thead>
                <tbody>
                  <for|rec| of=trend.province_records>
                    <tr>
                      <td>${rec.province}</td>
                      <td>${rec.subject_type}</td>
                      <td>${rec.batch_name}</td>
                      <td><span class="score-badge">${rec.min_score ?? "—"}</span></td>
                      <td>${rec.avg_score ?? "—"}</td>
                      <td>${rec.max_score ?? "—"}</td>
                      <td>${rec.min_rank ?? "—"}</td>
                    </tr>
                  </for>
                </tbody>
              </table>
            </div>
          </if>
        </div>
      </for>
    </div>
  </section>
</if>

<if=data.score_trends && data.score_trends.length>
  <for|trend| of=data.score_trends>
    <score-trend-chart trend=trend />
  </for>
</if>

<section class="section-block">
  <div class="grid-container">
    <div class="grid-x grid-margin-x">
      <div class="cell large-7">
        <div class="section-header">
          <div>
            <h2 class="section-title">省份覆盖</h2>
            <p class="section-note">优先展示记录更完整的省份，用于判断该校在不同地区的样本密度。</p>
          </div>
          <a href=(`${provincesPath}`)>全部省份</a>
        </div>
        <div class="data-panel table-wrap">
          <table class="stack">
            <thead>
              <tr>
                <th>省份</th>
                <th>最新最低分</th>
                <th>年份数</th>
                <th>专业数</th>
                <th>最新年份</th>
              </tr>
            </thead>
            <tbody>
              <for|item| of=provinceSummaries>
                <tr>
                  <td><a href=(provincesPath)>${item.province}</a></td>
                  <td><span class="score-badge">${item.latest_min_score ?? "—"}</span></td>
                  <td>${item.year_count}</td>
                  <td>${item.major_count}</td>
                  <td>${item.latest_year}</td>
                </tr>
              </for>
            </tbody>
          </table>
        </div>
      </div>
      <div class="cell large-5">
        <div class="section-header">
          <div>
            <h2 class="section-title">专业分数门槛</h2>
            <p class="section-note">按最新年份最低分升序排列，帮助判断哪些专业相对容易进入。</p>
          </div>
          <a href=(`${majorsPath}`)>全部专业</a>
        </div>
        <div class="content-panel">
          <for|major| of=majorSummaries>
            <div class="grid-x align-middle">
              <div class="cell auto">
                <strong><a href=(majorsPath)>${major.major_name}</a></strong>
                <div class="section-note">${major.province_count} 省 / ${major.year_count} 年</div>
              </div>
              <div class="cell shrink"><span class="score-badge">${major.latest_min_score ?? "—"}</span></div>
            </div>
            <hr>
          </for>
        </div>
      </div>
    </div>
  </div>
</section>

<section class="section-block">
  <div class="grid-container">
    <div class="section-header">
      <div>
        <h2 class="section-title">${summary.latest_year} 年重点专业概览</h2>
        <p class="section-note">按最低分降序选取，用于观察最新年份的高位专业分布。</p>
      </div>
    </div>
    <div class="grid-x grid-margin-x grid-margin-y small-up-1 medium-up-2 large-up-3">
      <for|record| of=featuredRecords>
        <div class="cell">
          <article class="spotlight-card">
            <div class="kicker">${record.province} / ${record.subject_type}</div>
            <h3>${record.major_name}</h3>
            <p class="score-line"><span class="score-badge">${record.min_score}</span></p>
            <ul class="subtle-list">
              <li>批次：${record.batch_name}</li>
              <li>最高分：${record.max_score ?? "—"}</li>
              <li>平均分：${record.avg_score ?? "—"}</li>
            </ul>
          </article>
        </div>
      </for>
    </div>
  </div>
</section>

<section class="section-block">
  <div class="grid-container">
    <div class="section-header">
      <div>
        <h2 class="section-title">${institution.name} 常见问题</h2>
        <p class="section-note">以下问题围绕样本规模、年份范围和后续浏览路径展开，适合作为首轮判断依据。</p>
      </div>
    </div>
    <div class="grid-x grid-margin-x grid-margin-y small-up-1 medium-up-2">
      <div class="cell">
        <article class="spotlight-card">
          <h3>${institution.name}目前覆盖多少省份和年份的录取数据？</h3>
          <p class="section-note">
            当前覆盖<a href=(provincesPath)>${summary.province_count}个省份</a>、<a href=(yearsPath)>${summary.year_count}个年份</a>，累计${summary.record_count}条录取记录。
          </p>
        </article>
      </div>
      <div class="cell">
        <article class="spotlight-card">
          <h3>${institution.name}最新年份的录取数据是哪一年？</h3>
          <p class="section-note">
            当前页面最新年份为${summary.latest_year}年，可进入<a href=(yearsPath)>年份页面</a>核对历年记录规模和覆盖情况。
          </p>
        </article>
      </div>
      <div class="cell">
        <article class="spotlight-card">
          <h3>${institution.name}应优先浏览哪个维度？</h3>
          <p class="section-note">
            如处于初步筛选阶段，建议优先浏览<a href=(basePath)>总览页</a>和<a href=(yearsPath)>年份页</a>；如已明确目标院校，再进入<a href=(provincesPath)>省份页</a>和<a href=(majorsPath)>专业页</a>。
          </p>
        </article>
      </div>
      <div class="cell">
        <article class="spotlight-card">
          <h3>${institution.name}总览页主要承担什么作用？</h3>
          <p class="section-note">
            该页用于集中展示历年录取样本、<a href=(majorsPath)>重点专业</a>、<a href=(provincesPath)>省份覆盖</a>和<a href=(yearsPath)>年度分布</a>，为后续院校比较提供基础判断依据。
          </p>
        </article>
      </div>
    </div>
  </div>
</section>

<institution-data-source
  generatedAt=data.generated_at
  sourceDomains=(extractSourceDomains([...data.featured_records, ...data.latest_records]))
  recordCount=data.summary.record_count
/>

<if=relatedInstitutions.length>
  <section class="section-block">
    <div class="grid-container">
      <div class="section-header">
        <div>
          <h2 class="section-title">相关院校</h2>
          <p class="section-note">优先展示同省份、同院校类型或同层次标签的学校，帮助搜索用户继续浏览。</p>
        </div>
      </div>
      <div class="grid-x grid-margin-x grid-margin-y small-up-1 medium-up-2 large-up-3">
        <for|related| of=relatedInstitutions>
          <const/relatedDesignationNames=(related.designations || []).map(designation => designation.name)>
          <const/classificationText=([...relatedDesignationNames, related.category].filter(Boolean).join(" · ") || "院校类型待补充")>
          <const/relatedShortlistEntry=({
            key: related.path,
            path: related.path,
            name: related.name,
            institution_province: related.province,
            city: related.city,
            category: related.category,
            designation_summary: relatedDesignationNames.join(" · "),
            record_count: related.record_count,
            province_count: related.province_count,
            major_count: related.major_count,
            year_count: related.year_count,
            latest_year: related.latest_year,
            source_label: "相关院校",
          })>
          <div class="cell">
            <article class="spotlight-card institution-card">
              <span class="shortlist-badge" data-shortlist-toggle data-shortlist-key=(related.path) aria-label="加入候选">+候选</span>
              <h3>${related.name}<span class="institution-location">${related.province} · ${related.city}</span></h3>
              <p class="section-note">${classificationText}</p>
              <div class="grid-x grid-margin-x small-up-2 stats-tight">
                <div class="cell">
                  <div class="stat-value stat-value-small">${related.record_count}</div>
                  <div class="stat-label">记录</div>
                </div>
                <div class="cell">
                  <div class="stat-value stat-value-small">${related.latest_year}</div>
                  <div class="stat-label">最新年份</div>
                </div>
              </div>
              <div class="hero-actions">
                <a class="button ghost expanded" href=(`${related.path}`)>查看院校页</a>
              </div>
            </article>
          </div>
        </for>
      </div>
    </div>
  </section>
</if>

<html-script type="application/ld+json">$!{faqStructuredData}</html-script>
